/**
 * Parses TSV/CSV test concept data and classifies columns as condition or value using a heuristic:
 * columns with one distinct value per group are conditions, columns with multiple distinct values
 * are value columns.
 */

export type ColumnRole = "condition" | "value" | "ignore"

export interface ColumnClassification {
  name: string
  role: ColumnRole
  distinctPerGroup: number // avg distinct values per group
}

interface ParsedTestConceptGroup {
  /**
   * Condition column values that define this group.
   */
  conditions: Record<string, string>
  /**
   * Value columns: column name -> set of allowed values.
   */
  values: Record<string, string[]>
}

/**
 * Parse raw text (TSV or CSV) into headers and rows.
 */
export function parseText(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim())
  if (lines.length < 2) return { headers: [], rows: [] }
  const delimiter = lines[0].includes("\t") ? "\t" : ","
  const headers = lines[0].split(delimiter).map((h) => h.trim().replace(/^["']|["']$/g, ""))
  const rows: string[][] = []
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(delimiter).map((c) => c.trim().replace(/^["']|["']$/g, ""))
    rows.push(cols)
  }
  return { headers, rows }
}

/**
 * Classify columns as condition or value using the heuristic: - All same value across all rows →
 * ignore - Group by the combination of all low-cardinality columns, then: columns with 1 value per
 * group → condition columns with multiple values per group → value.
 */
export function classifyColumns(headers: string[], rows: string[][]): ColumnClassification[] {
  if (headers.length === 0 || rows.length === 0) return []

  const distinctCounts = headers.map((_, colIdx) => {
    const vals = new Set<string>()
    for (const row of rows) vals.add(row[colIdx] || "")
    return vals.size
  })

  // Candidate condition columns: distinct values > 1 but much less than row count
  // Use threshold: distinct < sqrt(rows) or distinct <= rows/3
  const threshold = Math.max(Math.sqrt(rows.length), rows.length / 3)
  const condCandidates: number[] = []
  for (let i = 0; i < headers.length; i++) {
    if (distinctCounts[i] > 1 && distinctCounts[i] <= threshold) {
      condCandidates.push(i)
    }
  }

  // Group by the combination of all candidate condition columns
  const groupMap = new Map<string, string[][]>()
  for (const row of rows) {
    const key = condCandidates.map((ci) => row[ci] || "").join("\x00")
    if (!groupMap.has(key)) groupMap.set(key, [])
    groupMap.get(key)!.push(row)
  }
  const numGroups = groupMap.size || 1

  return headers.map((name, colIdx) => {
    if (distinctCounts[colIdx] <= 1) {
      return { name, role: "ignore" as ColumnRole, distinctPerGroup: 1 }
    }

    let totalDistinct = 0
    for (const groupRows of groupMap.values()) {
      const vals = new Set<string>()
      for (const row of groupRows) vals.add(row[colIdx] || "")
      totalDistinct += vals.size
    }
    const avgDistinct = totalDistinct / numGroups

    const role: ColumnRole = avgDistinct <= 1.2 ? "condition" : "value"
    return { name, role, distinctPerGroup: Math.round(avgDistinct * 10) / 10 }
  })
}

/**
 * Group rows by condition columns and collect value column entries.
 */
export function groupByConditions(
  headers: string[],
  rows: string[][],
  columns: ColumnClassification[],
): ParsedTestConceptGroup[] {
  const conditionCols = columns
    .map((c, i) => ({ ...c, idx: i }))
    .filter((c) => c.role === "condition")
  const valueCols = columns.map((c, i) => ({ ...c, idx: i })).filter((c) => c.role === "value")

  if (valueCols.length === 0) return []

  const groupMap = new Map<string, ParsedTestConceptGroup>()

  for (const row of rows) {
    const condKey = conditionCols.map((c) => row[c.idx] || "").join("\x00")
    if (!groupMap.has(condKey)) {
      const conditions: Record<string, string> = {}
      for (const c of conditionCols) conditions[headers[c.idx]] = row[c.idx] || ""
      const values: Record<string, string[]> = {}
      for (const v of valueCols) values[headers[v.idx]] = []
      groupMap.set(condKey, { conditions, values })
    }
    const group = groupMap.get(condKey)!
    for (const v of valueCols) {
      const val = row[v.idx] || ""
      if (val && !group.values[headers[v.idx]].includes(val)) {
        group.values[headers[v.idx]].push(val)
      }
    }
  }

  return Array.from(groupMap.values())
}
